// Reorders table columns via drag-and-drop on <th data-col="key"> handles.
// Column order is persisted per table (data-table-id) in localStorage.
//
// Expectations on the markup:
//   <table phx-hook="DraggableTable" data-table-id="students">
//     <thead>
//       <tr>
//         <th data-col="name" draggable="true">…</th>
//         <th data-col="status" draggable="true">…</th>
//       </tr>
//     </thead>
//     <tbody>
//       <tr>
//         <td data-col="name">…</td>
//         <td data-col="status">…</td>
//       </tr>
//     </tbody>
//   </table>
//
// Columns without data-col (e.g., leading checkbox or trailing actions) are
// treated as "fixed" and skipped by the reorder logic — they always keep
// their DOM position.

type HookCtx = { el: HTMLTableElement }

const STORAGE_PREFIX = "dt:col-order:"

const storageKey = (table: HTMLTableElement) => {
  const id = table.dataset.tableId
  return id ? `${STORAGE_PREFIX}${id}` : null
}

const loadOrder = (table: HTMLTableElement): string[] | null => {
  const key = storageKey(table)
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const saveOrder = (table: HTMLTableElement, order: string[]) => {
  const key = storageKey(table)
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify(order))
  } catch {
    /* quota exceeded or storage disabled — ignore */
  }
}

const currentHeaderCols = (table: HTMLTableElement): string[] => {
  const row = table.querySelector("thead > tr")
  if (!row) return []
  return Array.from(row.querySelectorAll("th[data-col]")).map(
    th => (th as HTMLElement).dataset.col!,
  )
}

const reorderRow = (row: Element, desired: string[]) => {
  // Keep non-data-col cells in place; only shuffle the ones that have data-col.
  const cells = Array.from(row.children) as HTMLElement[]
  const dataCellSlots: number[] = []
  const cellByCol = new Map<string, HTMLElement>()
  cells.forEach((cell, idx) => {
    const col = cell.dataset.col
    if (col) {
      dataCellSlots.push(idx)
      cellByCol.set(col, cell)
    }
  })
  if (dataCellSlots.length !== desired.length) return
  desired.forEach((col, i) => {
    const slotIdx = dataCellSlots[i]
    const target = cellByCol.get(col)
    if (!target) return
    const current = row.children[slotIdx]
    if (current !== target) {
      row.insertBefore(target, current)
    }
  })
}

const applyOrder = (table: HTMLTableElement, desired: string[]) => {
  const headerRow = table.querySelector("thead > tr")
  if (headerRow) reorderRow(headerRow, desired)
  table.querySelectorAll("tbody > tr").forEach(row => reorderRow(row, desired))
}

export const DraggableTable = {
  mounted(this: HookCtx) {
    const table = this.el
    if (!(table instanceof HTMLTableElement)) return

    // Restore persisted order on mount
    const saved = loadOrder(table)
    if (saved && saved.length) {
      const cols = currentHeaderCols(table)
      const stillValid =
        saved.length === cols.length && saved.every(c => cols.includes(c))
      if (stillValid) applyOrder(table, saved)
    }

    this._attachHandlers()
  },

  updated(this: HookCtx & { _attachHandlers: () => void }) {
    // tbody rebuilds whenever LiveView patches — re-apply current order so
    // new rows match the user's column arrangement.
    const table = this.el as HTMLTableElement
    const cols = currentHeaderCols(table)
    if (cols.length) applyOrder(table, cols)
    this._attachHandlers()
  },

  _attachHandlers(this: HookCtx) {
    const table = this.el as HTMLTableElement
    const headers = Array.from(
      table.querySelectorAll<HTMLTableCellElement>("thead th[data-col]"),
    )

    let dragSrcCol: string | null = null

    headers.forEach(th => {
      if (th.dataset.dtWired === "1") return
      th.dataset.dtWired = "1"
      th.setAttribute("draggable", "true")

      th.addEventListener("dragstart", e => {
        dragSrcCol = th.dataset.col ?? null
        th.classList.add("opacity-50")
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = "move"
          e.dataTransfer.setData("text/plain", dragSrcCol ?? "")
        }
      })

      th.addEventListener("dragend", () => {
        th.classList.remove("opacity-50")
        headers.forEach(h => h.classList.remove("ring-2", "ring-primary/60"))
        dragSrcCol = null
      })

      th.addEventListener("dragover", e => {
        e.preventDefault()
        if (e.dataTransfer) e.dataTransfer.dropEffect = "move"
      })

      th.addEventListener("dragenter", () => {
        if (!dragSrcCol || dragSrcCol === th.dataset.col) return
        th.classList.add("ring-2", "ring-primary/60")
      })

      th.addEventListener("dragleave", () => {
        th.classList.remove("ring-2", "ring-primary/60")
      })

      th.addEventListener("drop", e => {
        e.preventDefault()
        th.classList.remove("ring-2", "ring-primary/60")
        if (!dragSrcCol) return
        const destCol = th.dataset.col
        if (!destCol || destCol === dragSrcCol) return

        const order = currentHeaderCols(table)
        const from = order.indexOf(dragSrcCol)
        const to = order.indexOf(destCol)
        if (from < 0 || to < 0) return

        order.splice(to, 0, order.splice(from, 1)[0])
        applyOrder(table, order)
        saveOrder(table, order)
      })
    })
  },
}
